import React from 'react';
import { ChatCircle, Trash, Spinner, PaperPlaneTilt, DownloadSimple } from '@phosphor-icons/react';
import { API } from '@/lib/api';

const SUGGESTIONS = [
  { label: '📄 Investigation Report', prompt: 'Draft an Investigation Report based on the case so far. Intended audience: school board. Goal: formal record of concerns.' },
  { label: '🗓 Chronology', prompt: 'Build a Chronology document of all key events with dates, actors, and source citations from the uploaded documents.' },
  { label: '✉ Investigation Request', prompt: 'Draft a formal Investigation Request letter requesting that the appropriate authority open an investigation, summarising the concerns.' },
  { label: '📝 Witness Statement', prompt: 'Help me draft a Witness Statement template. Ask me clarifying questions first about who the witness is and what they observed.' },
  { label: '✉ Complaint Letter', prompt: 'Draft a Complaint Letter to the most relevant government agency from our legal panel.' },
];

const bubbleClass = (m) => {
  if (m.role === 'user') return 'bg-[#002FA7] text-white';
  if (m._error) return 'bg-red-50 text-red-800 border border-red-200';
  return 'bg-white text-[#09090B] border border-[#E5E7EB]';
};

export const ConcernChatThread = ({
  caseId, chatMessages, chatInput, setChatInput, chatLoading, sendChat, clearChat, clearingChat, userConcern, chatEndRef,
}) => (
  <div className="mt-5 pt-5 border-t border-[#E5E7EB]" data-testid="concern-chat-thread">
    <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
      <h4 className="font-outfit text-base font-semibold text-[#09090B] flex items-center gap-2">
        <ChatCircle size={18} className="text-[#002FA7]" weight="fill" /> Talk to AI
        {chatMessages.length > 0 && (
          <span className="text-xs font-ibm text-[#6B7280] font-normal">· {chatMessages.length} message{chatMessages.length === 1 ? '' : 's'}</span>
        )}
      </h4>
      {chatMessages.length > 0 && (
        <button onClick={clearChat} disabled={clearingChat} data-testid="concern-chat-clear"
          className="flex items-center gap-1.5 px-2.5 py-1 text-xs text-[#6B7280] hover:text-red-600 font-ibm border border-[#E5E7EB] rounded-md hover:border-red-200 transition-all disabled:opacity-50">
          <Trash size={12} /> Clear chat
        </button>
      )}
    </div>
    <p className="text-xs text-[#6B7280] font-ibm mb-3">Ask follow-up questions, share more details, or ask the AI to clarify. Same strict rules apply — answers cite your documents and the legal panel only.</p>

    {/* Suggestion chips: kick off common document-generation requests */}
    <div className="flex flex-wrap gap-1.5 mb-3" data-testid="concern-chat-suggestions">
      {SUGGESTIONS.map((s, idx) => (
        <button key={s.label} type="button" onClick={() => setChatInput(s.prompt)}
          disabled={chatLoading || !userConcern}
          className="text-xs font-ibm px-2.5 py-1 rounded-full border border-[#E5E7EB] text-[#09090B] hover:bg-[#F3F4F6] hover:border-[#002FA7] hover:text-[#002FA7] transition-all disabled:opacity-50"
          data-testid={`chat-suggestion-${idx}`}>
          {s.label}
        </button>
      ))}
    </div>

    <div className="border border-[#E5E7EB] rounded-md bg-[#F9FAFB] p-3 max-h-[420px] overflow-y-auto" data-testid="concern-chat-scroll">
      {chatMessages.length === 0 && !chatLoading && (
        <div className="text-center py-6">
          <ChatCircle size={28} className="mx-auto text-[#E5E7EB] mb-2" />
          <p className="text-sm font-ibm text-[#6B7280]">Start a conversation. Try: <span className="italic">"Is there evidence of retaliation after the complaint?"</span></p>
        </div>
      )}
      {chatMessages.map((m, i) => (
        <div key={m._id || `${m.role}-${m.created_at || i}`} className={`mb-3 flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`} data-testid={`concern-chat-msg-${i}`}>
          <div className={`max-w-[85%] px-3 py-2 rounded-lg text-sm font-ibm whitespace-pre-wrap shadow-sm ${bubbleClass(m)}`}>
            {m.content}
            {m.generated_document_id && (
              <div className="mt-2 pt-2 border-t border-[#E5E7EB] flex flex-wrap gap-2" data-testid={`chat-gen-doc-${m.generated_document_id}`}>
                <a href={`${API}/cases/${caseId}/generated-documents/${m.generated_document_id}/download?fmt=docx`} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-ibm font-semibold bg-[#002FA7] text-white rounded-md hover:bg-[#00237D] transition-all"
                  data-testid={`chat-download-docx-${m.generated_document_id}`}>
                  <DownloadSimple size={12} /> .docx
                </a>
                <a href={`${API}/cases/${caseId}/generated-documents/${m.generated_document_id}/download?fmt=pdf`} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-ibm font-semibold bg-[#FF9500] text-white rounded-md hover:bg-[#E08600] transition-all"
                  data-testid={`chat-download-pdf-${m.generated_document_id}`}>
                  <DownloadSimple size={12} /> .pdf
                </a>
              </div>
            )}
            {m.created_at && (
              <div className={`text-[10px] mt-1 ${m.role === 'user' ? 'text-blue-100' : 'text-[#9CA3AF]'}`}>
                {new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </div>
            )}
          </div>
        </div>
      ))}
      {chatLoading && (
        <div className="flex justify-start mb-2">
          <div className="px-3 py-2 rounded-lg bg-white border border-[#E5E7EB] flex items-center gap-2">
            <Spinner size={14} className="animate-spin text-[#002FA7]" />
            <span className="text-xs font-ibm text-[#6B7280]">AI is thinking…</span>
          </div>
        </div>
      )}
      <div ref={chatEndRef} />
    </div>

    <form onSubmit={sendChat} className="mt-3 flex items-end gap-2">
      <textarea data-testid="concern-chat-input" value={chatInput} onChange={e => setChatInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendChat(e); } }}
        placeholder={userConcern ? "Ask a follow-up or share more details…" : "Add a concern description to this case first…"}
        rows={2}
        disabled={chatLoading || !userConcern}
        className="flex-1 px-3 py-2 border border-[#E5E7EB] rounded-md text-sm font-ibm focus:outline-none focus:ring-1 focus:ring-[#002FA7] focus:border-[#002FA7] resize-y disabled:opacity-50" />
      <button type="submit" disabled={chatLoading || !chatInput.trim() || !userConcern} data-testid="concern-chat-send"
        className="flex items-center gap-1.5 px-4 py-2 bg-[#002FA7] text-white font-ibm font-semibold text-sm rounded-md hover:bg-[#00237D] transition-all disabled:opacity-50 self-stretch">
        {chatLoading ? <Spinner size={14} className="animate-spin" /> : <PaperPlaneTilt size={14} weight="fill" />}
        <span>Send</span>
      </button>
    </form>
    <p className="text-[10px] text-[#9CA3AF] font-ibm mt-1">Press Enter to send · Shift+Enter for new line</p>
  </div>
);
